import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Activity,
  Users,
  User,
  ShoppingCart,
  Truck,
  Package,
  BarChart3,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  Shield,
  MapPin,
  Crown,
  UserCog,
  Headphones,
  UserCheck,
  Building,
  Phone,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Globe,
} from "lucide-react";
import { OrdersMain } from "../pages/Orders";
import { UsersMain } from "../pages/Users/UsersMain";
import Settings from "../pages/Settings";
import { DashboardMain } from "../pages/Dashboard";
import { API_BASE_URL } from "../utils/apiConfig";
import ShippingManagement from "./ShippingManagement";
import NaturatherapyWebManagement from "./NaturatherapyWebManagement";
import WooCommerceIcon from "./WooCommerceIcon";
import Analytics from "./Analytics";
// AlterCPA removed for Serbia-CC
// import { AlterCPAMain } from "../pages/AlterCPA";
import { WooCommerceMain } from "../pages/WooCommerce";
// Guarantees and Marketing removed for Serbia-CC
// import GuaranteesMain from "../pages/Guarantee/GuaranteesMain";
import { ProductsMain } from "../pages/Product";
// import MarketingMain from "../pages/Marketing/MarketingMain";
// Files removed for Serbia-CC (was Active Directory dependent)
// import FilesMain from "../pages/Files/FilesMain";
import EnhancedChat from "./EnhancedChat";
import { CallCenterMain } from "../pages/CallCenter";
import { LeadsMain } from "../pages/Leads";
import LeadDetailPage from "../pages/Leads/LeadDetailPage";
import { useLanguage } from "../contexts/LanguageContext";

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  countries: string[];
  permissions: string[];
  achievements?: string[]; // Task achievements for call operators
  profile: {
    firstName: string;
    lastName: string;
    phone: string;
    department: string;
  };
}

interface AuthenticatedAppProps {
  user: User;
  onLogout: () => void;
}

const AuthenticatedApp: React.FC<AuthenticatedAppProps> = ({
  user,
  onLogout,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Accordion state for settings sections
  const [accordionState, setAccordionState] = useState({
    configuration: true,
  });

  const toggleAccordion = (section: keyof typeof accordionState) => {
    setAccordionState((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Redirect to first accessible section if on root path
  useEffect(() => {
    if (location.pathname === "/") {
      // For website-post-manager, go directly to naturatherapy-web
      if (user.role === "website-post-manager" && user.permissions.includes("naturatherapy-web.view")) {
        navigate("/naturatherapy-web", { replace: true });
      }
      // For call-operator, skip dashboard, orders, and analytics, go directly to call-center
      else if (user.role === "call-operator" && user.permissions.includes("call-center.view")) {
        navigate("/call-center", { replace: true });
      } else if (user.permissions.includes("dashboard.view") && user.role !== "call-operator") {
        navigate("/dashboard", { replace: true });
      } else if (user.permissions.includes("orders.view") && user.role !== "call-operator") {
        navigate("/orders", { replace: true });
      } else if (user.permissions.includes("call-center.view")) {
        navigate("/call-center", { replace: true });
      } else if (user.permissions.includes("shipping.view")) {
        navigate("/shipping", { replace: true });
      } else {
        navigate("/call-center", { replace: true });
      }
    }
  }, [location.pathname, user.permissions, user.role, navigate]);

  // Protected route wrapper for routes hidden from call-operator and website-post-manager
  const ProtectedRoute: React.FC<{ children: React.ReactElement; path: string }> = ({ children, path }) => {
    const callOperatorRestrictedPaths = [
      "/dashboard",
      "/analytics",
      "/orders",
      "/leads",
      "/shipping",
      "/naturatherapy-web",
      "/woocommerce",
      "/products",
      "/users"
    ];

    const websitePostManagerRestrictedPaths = [
      "/dashboard",
      "/analytics",
      "/orders",
      "/leads",
      "/call-center",
      "/shipping",
      "/woocommerce",
      "/products",
      "/users"
    ];

    useEffect(() => {
      if (user.role === "call-operator" && callOperatorRestrictedPaths.includes(path)) {
        navigate("/call-center", { replace: true });
      } else if (user.role === "website-post-manager" && websitePostManagerRestrictedPaths.includes(path)) {
        navigate("/naturatherapy-web", { replace: true });
      }
    }, [path, user.role, navigate]);

    if (user.role === "call-operator" && callOperatorRestrictedPaths.includes(path)) {
      return null;
    }
    if (user.role === "website-post-manager" && websitePostManagerRestrictedPaths.includes(path)) {
      return null;
    }
    return children;
  };

  // Role-based navigation items
  const getNavigationItems = () => {
    const items = [];

    // For website-post-manager, only show Naturatherapy Web
    if (user.role === "website-post-manager") {
      if (user.permissions.includes("naturatherapy-web.view")) {
        items.push({
          id: "naturatherapy-web",
          icon: <WooCommerceIcon className="w-5 h-5" />,
          permission: "naturatherapy-web.view",
        });
      }
      return items;
    }

    // Dashboard - requires dashboard.view permission, but hidden for call-operator role
    if (user.permissions.includes("dashboard.view") && user.role !== "call-operator") {
      items.push({
        id: "dashboard",
        icon: <Activity className="w-5 h-5" />,
        permission: "dashboard.view",
      });
    }

    // Orders - available to most roles, but hidden for call-operator
    if (user.permissions.includes("orders.view") && user.role !== "call-operator") {
      items.push({
        id: "orders",
        icon: <ShoppingCart className="w-5 h-5" />,
        permission: "orders.view",
      });
    }

    // Call Center - requires call-center.view permission
    if (user.permissions.includes("call-center.view")) {
      items.push({
        id: "call-center",
        icon: <Headphones className="w-5 h-5" />,
        permission: "call-center.view",
      });
    }

    // Leads - requires leads.view permission, but hidden for call-operator
    if (user.permissions.includes("leads.view") && user.role !== "call-operator") {
      items.push({
        id: "leads",
        icon: <UserCheck className="w-5 h-5" />,
        permission: "leads.view",
      });
    }

    // Shipping - for post managers and above (includes Pickup Lists as tab), but hidden for call-operator
    if (user.permissions.includes("shipping.view") && user.role !== "call-operator") {
      items.push({
        id: "shipping",
        icon: <Truck className="w-5 h-5" />,
        permission: "shipping.view",
      });
    }

    // Naturatherapy Web - for post managers and above (web orders only), but hidden for call-operator
    if (user.permissions.includes("naturatherapy-web.view") && user.role !== "call-operator") {
      items.push({
        id: "naturatherapy-web",
        icon: <WooCommerceIcon className="w-5 h-5" />,
        permission: "naturatherapy-web.view",
      });
    }

    // Analytics - for managers and above, but hidden for call-operator role
    if (user.permissions.includes("analytics.view") && user.role !== "call-operator") {
      items.push({
        id: "analytics",
        icon: <BarChart3 className="w-5 h-5" />,
        permission: "analytics.view",
      });
    }

    // WooCommerce - Hidden from all account types
    // if (user.permissions.includes("system.settings") && user.role !== "call-operator") {
    //   items.push({
    //     id: "woocommerce",
    //     name: "WooCommerce",
    //     icon: (
    //       <svg
    //         className="w-5 h-5"
    //         viewBox="0 -51.5 256 256"
    //         fill="currentColor"
    //         xmlns="http://www.w3.org/2000/svg"
    //       >
    //         <path
    //           d="M232.137523,0 L23.7592719,0 C10.5720702,0 -0.103257595,10.7799647 0.000639559736,23.862498 L0.000639559736,103.404247 C0.000639559736,116.591115 10.6767385,127.266612 23.8639402,127.266612 L122.558181,127.266612 L167.667206,152.384995 L157.410382,127.266612 L232.137523,127.266612 C245.325059,127.266612 255.999888,116.591115 255.999888,103.404247 L255.999888,23.862498 C255.999888,10.6752964 245.325059,0 232.137523,0 Z M19.3640061,18.4201481 C16.4334946,18.6294847 14.2355942,19.6761007 12.7703719,21.6645976 C11.3051496,23.5484931 10.781875,25.9556632 11.095813,28.6768382 C17.2707741,67.9247058 23.027062,94.4034429 28.3647436,108.113986 C30.4579757,113.137395 32.8651458,115.5451 35.690989,115.335764 C40.086656,115.021425 45.3196694,108.951332 51.4946305,97.1248185 C54.73908,90.4260478 59.7628237,80.3792294 66.5657276,66.9823567 C72.2170797,86.762992 79.9618646,101.625221 89.6956814,111.567705 C92.417057,114.393415 95.2427665,115.649434 97.9634733,115.440098 C100.371178,115.230761 102.254539,113.974742 103.510558,111.672039 C104.557241,109.683676 104.975914,107.380974 104.766578,104.764601 C104.138568,95.2407893 105.080917,81.9489193 107.69729,64.8892584 C110.417997,47.3063898 113.767382,34.6424627 117.849111,27.1069476 C118.686458,25.5370569 119.000128,23.9670994 118.895794,22.0832708 C118.686458,19.6760338 117.639775,17.6875369 115.651411,16.1176463 C113.663048,14.5477557 111.46468,13.8151445 109.057643,14.0244142 C106.022597,14.2337508 103.719895,15.6989731 102.150205,18.6294847 C95.6614396,30.4560654 91.0560348,49.6088916 88.335328,76.1924977 C84.3579328,66.145211 81.0085475,54.3185634 78.3921746,40.3987506 C77.2411578,34.2238564 74.4154483,31.2933449 69.8100434,31.6073497 C66.670329,31.8166194 64.0538892,33.9098515 61.9606572,37.8869122 L39.0401069,81.5302462 C35.2723159,66.3544807 31.7138615,47.8296643 28.4694119,25.9556632 C27.7368007,20.5133802 24.7016209,18.0014749 19.3640061,18.4201481 Z M221.044022,25.9559976 C228.475136,27.5259551 234.022221,31.5030159 237.789611,38.0965832 C241.138996,43.7482697 242.81302,50.5511737 242.81302,58.7146317 C242.81302,69.4943957 240.092314,79.3325464 234.649562,88.3333508 C228.370134,98.7995112 220.206676,104.032257 210.054854,104.032257 C208.275828,104.032257 206.391799,103.82292 204.402767,103.404247 C196.972321,101.834557 191.425236,97.857831 187.657177,91.264063 C184.308461,85.5076413 182.633768,78.6002028 182.633768,70.5410787 C182.633768,59.7612478 185.355144,49.923164 190.797226,41.0270948 C197.181658,30.5610681 205.345116,25.3280548 215.392603,25.3280548 C217.171629,25.3280548 219.055659,25.5373913 221.044022,25.9559976 Z M216.648622,82.5769291 C220.521015,79.1232099 223.137388,73.9947979 224.602744,67.0873594 C225.021417,64.6802562 225.335087,62.0637496 225.335087,59.3425746 C225.335087,56.3074616 224.707078,53.0630121 223.451058,49.8184957 C221.881368,45.7367667 219.788002,43.5389332 217.275963,43.0155917 C213.508574,42.2829805 209.845518,44.3762126 206.391799,49.5045577 C203.566089,53.4816184 201.786394,57.6680157 200.844713,61.9590813 C200.321038,64.3663182 200.111701,66.9830255 200.111701,69.5993984 C200.111701,72.6344445 200.739711,75.8788272 201.99573,79.1232099 C203.566089,83.2049389 205.658786,85.4026386 208.170825,85.9263145 C210.787198,86.4493215 213.612908,85.2983047 216.648622,82.5769291 Z M172.167608,38.0965832 C168.399549,31.5030159 162.74813,27.5259551 155.422019,25.9559976 C153.432987,25.5373913 151.549626,25.3280548 149.769931,25.3280548 C139.723112,25.3280548 131.559654,30.5610681 125.175223,41.0270948 C119.732472,49.923164 117.011765,59.7612478 117.011765,70.5410787 C117.011765,78.6002028 118.686458,85.5076413 122.035174,91.264063 C125.803233,97.857831 131.350318,101.834557 138.780763,103.404247 C140.769126,103.82292 142.653156,104.032257 144.432851,104.032257 C154.584672,104.032257 162.74813,98.7995112 169.027559,88.3333508 C174.47031,79.3325464 177.191017,69.4943957 177.191017,58.7146317 C177.191017,50.5511737 175.516324,43.7482697 172.167608,38.0965832 Z M158.980072,67.0873594 C157.515384,73.9947979 154.898343,79.1232099 151.02595,82.5769291 C147.990904,85.2983047 145.165195,86.4493215 142.548822,85.9263145 C140.036783,85.4026386 137.943417,83.2049389 136.373727,79.1232099 C135.117707,75.8788272 134.489698,72.6344445 134.489698,69.5993984 C134.489698,66.9830255 134.699034,64.3663182 135.22271,61.9590813 C136.16439,57.6680157 137.943417,53.4816184 140.769126,49.5045577 C144.223514,44.3762126 147.88657,42.2829805 151.65396,43.0155917 C154.165999,43.5389332 156.259365,45.7367667 157.829055,49.8184957 C159.085074,53.0630121 159.713084,56.3074616 159.713084,59.3425746 C159.713084,62.0637496 159.503748,64.6802562 158.980072,67.0873594 Z"
    //           fillRule="evenodd"
    //         />
    //       </svg>
    //     ),
    //     permission: "system.settings",
    //   });
    // }

    // AlterCPA removed for Serbia-CC
    // Guarantees removed for Serbia-CC
    // Marketing removed for Serbia-CC
    // Active Directory removed for Serbia-CC

    // Products - for managers and above, but hidden for call-operator
    if (user.permissions.includes("system.settings") && user.role !== "call-operator") {
      items.push({
        id: "products",
        icon: <Package className="w-5 h-5" />,
        permission: "system.settings",
      });
    }

    // User Management - for admins, managers, country managers, but hidden for call-operator
    if (user.permissions.includes("users.view") && user.role !== "call-operator") {
      items.push({
        id: "users",
        icon: <Users className="w-5 h-5" />,
        permission: "users.view",
      });
    }

    // System Settings - for admins and managers only
    if (user.permissions.includes("system.settings")) {
      items.push({
        id: "settings",
        icon: <SettingsIcon className="w-5 h-5" />,
        permission: "system.settings",
      });
    }

    return items;
  };

  const getRoleIcon = (role: string) => {
    const icons = {
      admin: <Crown className="w-4 h-4 text-purple-600" />,
      manager: <UserCog className="w-4 h-4 text-blue-600" />,
      "country-manager": <MapPin className="w-4 h-4 text-green-600" />,
      "country-post-manager": <Truck className="w-4 h-4 text-indigo-600" />,
      "call-center": <Headphones className="w-4 h-4 text-orange-600" />,
      "call-operator": <Phone className="w-4 h-4 text-blue-600" />,
      "post-operator": <Package className="w-4 h-4 text-purple-600" />,
      "website-post-manager": <Globe className="w-4 h-4 text-teal-600" />,
    };
    return (
      icons[role as keyof typeof icons] || (
        <Shield className="w-4 h-4 text-gray-600" />
      )
    );
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("authToken");
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      onLogout();
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <div className="h-[100dvh] flex bg-gray-50 overflow-hidden w-full">
      {/* Mobile sidebar overlay */}
      <div
        className={`fixed inset-0 flex z-40 lg:hidden transition-opacity duration-300 ${
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
        <div className={`relative flex-1 flex flex-col w-full sm:max-w-xs bg-[#264a3e] transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}>
          <div className="absolute top-0 right-0 -mr-12 pt-2 z-10">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors touch-manipulation"
              onClick={() => setSidebarOpen(false)}
              aria-label={t('nav.closeMenu')}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4 mb-5">
              <img
                src="/logo.svg"
                alt="Natura Therapy"
                className="h-10 sm:h-12 w-auto"
              />
            </div>
            <nav className="px-2 space-y-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  to={`/${item.id}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`${
                    location.pathname === `/${item.id}` || location.pathname.startsWith(`/${item.id}/`)
                      ? "bg-white bg-opacity-20 text-white"
                      : "text-white hover:bg-white hover:bg-opacity-10"
                  } group flex items-center px-3 py-3 text-sm font-medium rounded-md w-full text-left transition-colors touch-manipulation`}
                >
                  {item.icon}
                  <span className="ml-3">{t(item.id === 'call-center' ? 'nav.callCenter' : `nav.${item.id}`)}</span>
                </Link>
              ))}
            </nav>
          </div>
          {/* Mobile User info and logout */}
          <div className="flex-shrink-0 flex border-t border-white border-opacity-20 p-4">
            <div className="flex items-center w-full">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                  {getRoleIcon(user.role)}
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user.profile.firstName} {user.profile.lastName}
                </p>
                <p className="text-xs text-white text-opacity-70 truncate">
                  {user.role
                    .replace("-", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-3 flex-shrink-0 p-2 rounded-full text-white hover:bg-white hover:bg-opacity-20 transition-colors touch-manipulation"
                aria-label={t('nav.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Desktop sidebar - Full Height */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64 fixed h-[100dvh] bg-[#264a3e] z-30">
          {/* Logo Section */}
          <div className="flex items-center justify-center flex-shrink-0 px-2 py-3 border-b border-white border-opacity-20">
            <img src="/logo.svg" alt="Natura Therapy" className="h-16 w-auto" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => (
              <Link
                key={item.id}
                to={`/${item.id}`}
                className={`${
                  location.pathname === `/${item.id}` || location.pathname.startsWith(`/${item.id}/`)
                    ? "bg-white bg-opacity-20 text-white"
                    : "text-white hover:bg-white hover:bg-opacity-10"
                } group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left transition-colors`}
              >
                {item.icon}
                <span className="ml-3">{t(item.id === 'call-center' ? 'nav.callCenter' : `nav.${item.id}`)}</span>
              </Link>
            ))}
          </nav>

          {/* User info and logout - Fixed at bottom */}
          <div className="flex-shrink-0 flex border-t border-white border-opacity-20 p-4">
            <div className="flex items-center w-full">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                  {getRoleIcon(user.role)}
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-white">
                  {user.profile.firstName} {user.profile.lastName}
                </p>
                <p className="text-xs text-white text-opacity-70">
                  {user.role
                    .replace("-", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-3 flex-shrink-0 p-1 rounded-full text-white hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col lg:ml-64 w-full">
        {/* Mobile header */}
        <div className="lg:hidden bg-[#264a3e] shadow-sm z-20 sticky top-0">
          <div className="flex items-center justify-between px-4 py-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-md transition-colors touch-manipulation"
                aria-label={t('nav.openMenu')}
              >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-white text-base sm:text-lg font-semibold truncate mx-2">
              Natura Therapy
            </h1>
            <button
              onClick={handleLogout}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-md transition-colors touch-manipulation flex-shrink-0"
              aria-label={t('nav.logout')}
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Main content - no fixed header, let component handle its own header */}
        <main className="flex-1 overflow-y-auto bg-gray-50 w-full pb-[env(safe-area-inset-bottom)]">
          <Routes>
            {/* Dashboard - redirects call-operator to call-center */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute path="/dashboard">
                  <DashboardMain currentUser={user} />
                </ProtectedRoute>
              } 
            />
            {/* Orders - redirects call-operator to call-center */}
            <Route 
              path="/orders" 
              element={
                <ProtectedRoute path="/orders">
                  <OrdersMain currentUser={user} />
                </ProtectedRoute>
              } 
            />
            <Route path="/call-center/*" element={<CallCenterMain currentUser={user} />} />
            {/* Leads - redirects call-operator to call-center */}
            <Route 
              path="/leads" 
              element={
                <ProtectedRoute path="/leads">
                  <LeadsMain currentUser={user} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/leads/:id" 
              element={
                <ProtectedRoute path="/leads">
                  <LeadDetailPage currentUser={user} />
                </ProtectedRoute>
              } 
            />
            {/* Shipping - redirects call-operator to call-center */}
            <Route 
              path="/shipping/*" 
              element={
                <ProtectedRoute path="/shipping">
                  <ShippingManagement currentUser={user} />
                </ProtectedRoute>
              } 
            />
            {/* Naturatherapy Web - for website-post-manager role */}
            <Route 
              path="/naturatherapy-web/*" 
              element={
                <ProtectedRoute path="/naturatherapy-web">
                  <NaturatherapyWebManagement currentUser={user} />
                </ProtectedRoute>
              } 
            />
            {/* Analytics - redirects call-operator to call-center */}
            <Route 
              path="/analytics/*" 
              element={
                <ProtectedRoute path="/analytics">
                  <Analytics />
                </ProtectedRoute>
              } 
            />
            {/* WooCommerce - redirects call-operator to call-center */}
            <Route 
              path="/woocommerce/*" 
              element={
                <ProtectedRoute path="/woocommerce">
                  <WooCommerceMain />
                </ProtectedRoute>
              } 
            />
            {/* Products - redirects call-operator to call-center */}
            <Route 
              path="/products/*" 
              element={
                <ProtectedRoute path="/products">
                  <ProductsMain />
                </ProtectedRoute>
              } 
            />
            {/* Users - redirects call-operator to call-center */}
            <Route 
              path="/users/*" 
              element={
                <ProtectedRoute path="/users">
                  <UsersMain />
                </ProtectedRoute>
              } 
            />
            <Route path="/settings" element={
              <div className="p-6">
                <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('settings.title')}</h1>
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="bg-white shadow rounded-lg">
                      <button
                        onClick={() => toggleAccordion("configuration")}
                        className="w-full p-4 border-b border-gray-200 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium text-gray-900 flex items-center">
                            <SettingsIcon className="w-5 h-5 mr-2" />
                            {t('settings.configuration')}
                          </h3>
                          {accordionState.configuration ? (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                      </button>
                      {accordionState.configuration && (
                        <div className="p-4">
                          <Settings />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            } />
            <Route path="*" element={
              <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                  <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('error.pageNotFound')}
                  </h3>
                  <p className="text-gray-600">
                    {t('error.pageNotFoundDescription')}
                  </p>
                </div>
              </div>
            } />
          </Routes>
        </main>
      </div>

      {/* Enhanced Chat Button - Temporarily hidden */}
      {/* <EnhancedChat /> */}
    </div>
  );
};

export default AuthenticatedApp;
